import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Save, AlertCircle, AlertTriangle, Trash2, CheckCircle2, Truck,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Textarea, Spinner, Pill } from "@/components/ui";
import { AssetPhotoGallery } from "@/features/assets/AssetPhotoGallery";
import { AssetDocuments } from "@/features/assets/AssetDocuments";   // 5 Nov 2026 — mig 510: rego, safety cert, insurance on the file
import { fmtDate, daysFromNow, urgencyFromDays } from "@/lib/dates";

// =============================================================================
// VehicleDetailScreen — /assets/vehicles/:vehicleId (edit) | /new (create)
//
// Single component handles create + edit + deactivate. Route params determine
// mode:
//   /assets/vehicles/new        → no :vehicleId param → create mode
//   /assets/vehicles/<uuid>     → :vehicleId = uuid   → edit mode
//
// Editable by manager+. Read-only view for lower roles (though the nav doesn't
// surface /assets for them; this is defence in depth).
//
// T-050 Part A. Power tools + rigging detail screens follow the same pattern
// in Parts B + C.
// =============================================================================

const VEHICLE_TYPES    = ["ute", "van", "truck", "ewp", "trailer", "other"] as const;
const FUEL_TYPES       = ["petrol", "diesel", "electric", "hybrid", "lpg"] as const;
const AU_STATES        = ["QLD", "NSW", "VIC", "TAS", "SA", "WA", "NT", "ACT"] as const;

interface VehicleForm {
  vehicle_code:      string;
  rego:              string;
  make:              string;
  model:             string;
  year:              string;   // kept as string for the input, parsed on save
  vin:               string;
  colour:            string;
  vehicle_type:      string;
  gvm_kg:            string;
  fuel_type:         string;
  toll_tag_number:   string;
  rego_expiry:       string;
  ctp_insurer:       string;
  ctp_expiry:        string;
  service_due_date:  string;
  service_due_km:    string;
  current_km:        string;
  garaged_at:        string;
  notes:             string;
}

const EMPTY_FORM: VehicleForm = {
  vehicle_code: "", rego: "", make: "", model: "", year: "", vin: "",
  colour: "", vehicle_type: "", gvm_kg: "", fuel_type: "", toll_tag_number: "",
  rego_expiry: "", ctp_insurer: "", ctp_expiry: "",
  service_due_date: "", service_due_km: "", current_km: "",
  garaged_at: "", notes: "",
};

export default function VehicleDetailScreen() {
  const { vehicleId } = useParams<{ vehicleId?: string }>();
  const isCreate      = !vehicleId;
  const user          = useRequireAuth();
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();
  const canEdit       = hasRole(user, "manager");

  const [form, setForm]             = useState<VehicleForm>(EMPTY_FORM);
  const [isActive, setIsActive]     = useState(true);
  const [saveError, setSaveError]   = useState<string | null>(null);
  const [dirty, setDirty]           = useState(false);

  // ---------------------------------------------------------------------------
  // Load vehicle (edit mode only)
  // ---------------------------------------------------------------------------
  const { data: vehicle, isLoading } = useQuery({
    queryKey: ["vehicle_detail", vehicleId],
    enabled:  !isCreate && !!vehicleId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from("vehicles")
        .select(`
          vehicle_id, vehicle_code, rego, make, model, year, vin, colour,
          vehicle_type, gvm_kg, fuel_type, toll_tag_number,
          assigned_to_employee_id, assigned_from,
          rego_expiry, ctp_insurer, ctp_expiry,
          service_due_date, service_due_km, current_km,
          garaged_at, is_active, notes, created_at, updated_at,
          assigned_to:employees!vehicles_assigned_to_employee_id_fkey(first_name, last_name)
        `)
        .eq("vehicle_id", vehicleId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Prefill form when vehicle loads
  useEffect(() => {
    if (!vehicle) return;
    setForm({
      vehicle_code:     vehicle.vehicle_code ?? "",
      rego:             vehicle.rego ?? "",
      make:             vehicle.make ?? "",
      model:            vehicle.model ?? "",
      year:             vehicle.year != null ? String(vehicle.year) : "",
      vin:              vehicle.vin ?? "",
      colour:           vehicle.colour ?? "",
      vehicle_type:     vehicle.vehicle_type ?? "",
      gvm_kg:           vehicle.gvm_kg != null ? String(vehicle.gvm_kg) : "",
      fuel_type:        vehicle.fuel_type ?? "",
      toll_tag_number:  vehicle.toll_tag_number ?? "",
      rego_expiry:      vehicle.rego_expiry ?? "",
      ctp_insurer:      vehicle.ctp_insurer ?? "",
      ctp_expiry:       vehicle.ctp_expiry ?? "",
      service_due_date: vehicle.service_due_date ?? "",
      service_due_km:   vehicle.service_due_km != null ? String(vehicle.service_due_km) : "",
      current_km:       vehicle.current_km != null ? String(vehicle.current_km) : "",
      garaged_at:       vehicle.garaged_at ?? "",
      notes:            vehicle.notes ?? "",
    });
    setIsActive(vehicle.is_active ?? true);
    setDirty(false);
  }, [vehicle]);

  const onField = <K extends keyof VehicleForm>(key: K, value: VehicleForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    if (saveError) setSaveError(null);
  };

  // ---------------------------------------------------------------------------
  // Save (insert or update)
  // ---------------------------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form.vehicle_code.trim()) throw new Error("Vehicle code is required");
      if (!form.rego.trim())         throw new Error("Rego is required");

      const payload = {
        vehicle_code:     form.vehicle_code.trim(),
        rego:             form.rego.trim(),
        make:             form.make.trim()   || null,
        model:            form.model.trim()  || null,
        year:             form.year   ? Number(form.year)   : null,
        vin:              form.vin.trim()    || null,
        colour:           form.colour.trim() || null,
        vehicle_type:     form.vehicle_type  || null,
        gvm_kg:           form.gvm_kg ? Number(form.gvm_kg) : null,
        fuel_type:        form.fuel_type     || null,
        toll_tag_number:  form.toll_tag_number.trim() || null,
        rego_expiry:      form.rego_expiry      || null,
        ctp_insurer:      form.ctp_insurer.trim() || null,
        ctp_expiry:       form.ctp_expiry       || null,
        service_due_date: form.service_due_date || null,
        service_due_km:   form.service_due_km ? Number(form.service_due_km) : null,
        current_km:       form.current_km ? Number(form.current_km) : null,
        garaged_at:       form.garaged_at.trim() || null,
        notes:            form.notes.trim() || null,
        is_active:        isActive,
        updated_by:       user?.auth_user_id ?? null,
      };

      if (isCreate) {
        const { data, error } = await supabase
          .from("vehicles")
          .insert({ ...payload, created_by: user?.auth_user_id ?? null })
          .select("vehicle_id")
          .single();
        if (error) throw error;
        return data.vehicle_id as string;
      } else {
        const { error } = await supabase
          .from("vehicles")
          .update(payload)
          .eq("vehicle_id", vehicleId!);
        if (error) throw error;
        return vehicleId!;
      }
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      queryClient.invalidateQueries({ queryKey: ["vehicle_detail", id] });
      queryClient.invalidateQueries({ queryKey: ["my_vehicles"] });
      if (isCreate) {
        navigate(`/assets/vehicles/${id}`);
      } else {
        setDirty(false);
      }
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                 : (err as { message?: string })?.message ?? "Save failed";
      setSaveError(msg);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("vehicles")
        .update({ is_active: false, updated_by: user?.auth_user_id ?? null })
        .eq("vehicle_id", vehicleId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      queryClient.invalidateQueries({ queryKey: ["vehicle_detail", vehicleId] });
      queryClient.invalidateQueries({ queryKey: ["my_vehicles"] });
      setIsActive(false);
    },
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!canEdit) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-sm text-text-muted">
            Editing assets is restricted to managers and directors.
          </div>
        </Card>
      </div>
    );
  }

  if (!isCreate && isLoading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Spinner size={20} /></div>;
  }

  if (!isCreate && !vehicle) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 flex items-start gap-3">
            <AlertTriangle size={18} className="text-warn flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Vehicle not found</div>
              <div className="text-sm text-text-muted">
                The vehicle_id in the URL doesn't match any record.
              </div>
              <Link to="/assets" className="text-accent text-sm hover:underline mt-2 inline-block">
                ← Back to assets
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const assignedName = vehicle?.assigned_to
    ? `${vehicle.assigned_to.first_name ?? ""} ${vehicle.assigned_to.last_name ?? ""}`.trim()
    : null;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto pb-24">
      <Link to="/assets" className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
        <ArrowLeft size={14} /> Back to assets
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Truck size={18} className="text-accent" />
            <h1 className="text-2xl font-semibold tracking-tight">
              {isCreate ? "New vehicle" : (form.vehicle_code || "Vehicle")}
            </h1>
            {!isCreate && (
              <Pill
                label={isActive ? "active" : "inactive"}
                tone={isActive ? "approved" : "lost"}
                small
              />
            )}
          </div>
          {!isCreate && form.rego && (
            <div className="text-text-muted text-[13px] font-mono mt-1">
              {form.rego} · {[form.year, form.make, form.model].filter(Boolean).join(" ")}
            </div>
          )}
          {!isCreate && assignedName && (
            <div className="text-text-muted text-[13px] mt-1">
              Assigned to <span className="text-text">{assignedName}</span>
              {vehicle?.assigned_from && <> since {fmtDate(vehicle.assigned_from)}</>}
              {" — "}
              <Link to="/assets" className="text-accent hover:underline">reassign on /assets</Link>
            </div>
          )}
        </div>
      </div>

      <Section title="Identity">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <Field label="Vehicle code *" hint="unique — e.g. CG-V01">
            <Input type="text" value={form.vehicle_code} onChange={e => onField("vehicle_code", e.target.value)} />
          </Field>
          <Field label="Rego *">
            <Input type="text" value={form.rego} onChange={e => onField("rego", e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title="Details">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <Field label="Make">
            <Input type="text" value={form.make} onChange={e => onField("make", e.target.value)} />
          </Field>
          <Field label="Model">
            <Input type="text" value={form.model} onChange={e => onField("model", e.target.value)} />
          </Field>
          <Field label="Year">
            <Input type="number" value={form.year} onChange={e => onField("year", e.target.value)} />
          </Field>
          <Field label="Colour">
            <Input type="text" value={form.colour} onChange={e => onField("colour", e.target.value)} />
          </Field>
          <Field label="Vehicle type">
            <Select value={form.vehicle_type} onChange={e => onField("vehicle_type", e.target.value)}>
              <option value="">—</option>
              {VEHICLE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </Select>
          </Field>
          <Field label="Fuel type">
            <Select value={form.fuel_type} onChange={e => onField("fuel_type", e.target.value)}>
              <option value="">—</option>
              {FUEL_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </Select>
          </Field>
          <Field label="VIN">
            <Input type="text" value={form.vin} onChange={e => onField("vin", e.target.value)} />
          </Field>
          <Field label="GVM (kg)">
            <Input type="number" value={form.gvm_kg} onChange={e => onField("gvm_kg", e.target.value)} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Toll tag" hint="e-Tag / Linkt number">
              <Input type="text" value={form.toll_tag_number} onChange={e => onField("toll_tag_number", e.target.value)} />
            </Field>
          </div>
        </div>
      </Section>

      <Section title="Compliance">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <Field label="Rego expires">
            <Input type="date" value={form.rego_expiry} onChange={e => onField("rego_expiry", e.target.value)} />
          </Field>
          <div className="md:col-span-1">
            <ExpiryHint d={form.rego_expiry} />
          </div>
          <Field label="CTP insurer">
            <Input type="text" value={form.ctp_insurer} onChange={e => onField("ctp_insurer", e.target.value)} />
          </Field>
          <Field label="CTP expires">
            <Input type="date" value={form.ctp_expiry} onChange={e => onField("ctp_expiry", e.target.value)} />
          </Field>
          <Field label="Service due date">
            <Input type="date" value={form.service_due_date} onChange={e => onField("service_due_date", e.target.value)} />
          </Field>
          <Field label="Service due (km)">
            <Input type="number" value={form.service_due_km} onChange={e => onField("service_due_km", e.target.value)} />
          </Field>
          <Field label="Current km">
            <Input type="number" value={form.current_km} onChange={e => onField("current_km", e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title="Location & notes">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Garaged at" hint="e.g. depot yard, home address">
              <Input type="text" value={form.garaged_at} onChange={e => onField("garaged_at", e.target.value)} />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Notes">
              <Textarea rows={3} value={form.notes} onChange={e => onField("notes", e.target.value)} />
            </Field>
          </div>
        </div>
      </Section>

      {!isCreate && (
        <Section title="Status">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="accent-accent"
              checked={isActive}
              onChange={e => { setIsActive(e.target.checked); setDirty(true); }}
            />
            <span className="text-[13px] text-text">
              Active — untick to retire this vehicle without deleting the record
            </span>
          </label>
        </Section>
      )}

      {/* Photos + documents sit ABOVE the save bar — 5 Nov 2026, James: "this floating box goes over the top of the details cards behind" */}
      {!isCreate && vehicle && (
        <div className="mt-6">
          <AssetPhotoGallery assetKind="vehicle" assetId={vehicle.vehicle_id} canEdit={canEdit} />
        </div>
      )}
      {!isCreate && vehicle && (
        <div className="mt-6">
          <AssetDocuments assetKind="vehicle" assetId={vehicle.vehicle_id} canEdit={canEdit} />
        </div>
      )}

      {/* Sticky save bar — last in the page, so it never covers a card */}
      <div className="sticky bottom-4 max-md:bottom-[calc(var(--nav-clear)+1rem)] mt-6 z-10">
        <Card className="shadow-lg">
          <div className="p-4 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1 text-sm">
              {saveError ? (
                <div className="flex items-center gap-2 text-danger">
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <span className="truncate">{saveError}</span>
                </div>
              ) : saveMutation.isSuccess && !dirty ? (
                <div className="flex items-center gap-2 text-ok">
                  <CheckCircle2 size={14} />
                  <span>{isCreate ? "Vehicle created." : "Saved."}</span>
                </div>
              ) : dirty ? (
                <span className="text-text-muted">Unsaved changes.</span>
              ) : (
                <span className="text-text-muted">{isCreate ? "Ready to create." : "No changes."}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isCreate && isActive && (
                <Button
                  variant="ghost"
                  icon={Trash2}
                  disabled={saveMutation.isPending || deactivateMutation.isPending}
                  onClick={() => {
                    if (confirm(`Deactivate ${form.vehicle_code}? The record stays in the audit log; it just won't appear in active lists.`)) {
                      deactivateMutation.mutate();
                    }
                  }}
                >
                  Deactivate
                </Button>
              )}
              <Link to="/assets">
                <Button variant="ghost" disabled={saveMutation.isPending}>Cancel</Button>
              </Link>
              <Button
                variant="primary"
                icon={Save}
                disabled={(!isCreate && !dirty) || saveMutation.isPending}
                onClick={() => { setSaveError(null); saveMutation.mutate(); }}
              >
                {saveMutation.isPending
                  ? (isCreate ? "Creating…" : "Saving…")
                  : (isCreate ? "Create vehicle" : "Save changes")}
              </Button>
            </div>
          </div>
        </Card>

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="mb-4">
      <div className="p-6">
        <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-4">
          {title}
        </h2>
        {children}
      </div>
    </Card>
  );
}

function ExpiryHint({ d }: { d: string }) {
  if (!d) return null;
  const days = daysFromNow(d);
  const u = urgencyFromDays(days);
  if (u === "ok" || u === "notice") return null;
  const color = u === "expired" || u === "critical" ? "text-danger" : "text-warn";
  return (
    <div className={`text-[11px] ${color} mt-2 flex items-center gap-1`}>
      <AlertTriangle size={12} />
      <span>
        {days != null && days < 0 ? `${Math.abs(days)} days overdue` :
         days != null ? `${days} days remaining` : ""}
      </span>
    </div>
  );
}
